import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Layers, Search, Factory } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtNum } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Bases() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const [bases, setBases] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [produceFor, setProduceFor] = useState(null);
  const [batches, setBatches] = useState(1);
  const [produceOutlet, setProduceOutlet] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, i, m] = await Promise.all([
        api.get("/bases", { params: q ? { q } : {} }),
        api.get("/ingredients"),
        api.get("/meta/all"),
      ]);
      setBases(b.data);
      setIngredients(i.data);
      setUnits(m.data.units);
      setOutlets(m.data.outlets || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const ingInfo = (id) => ingredients.find((i) => i.id === id) || {};
  const ingPrice = (id) => { const i = ingInfo(id); return (i.avg_cost > 0 ? i.avg_cost : i.price) || 0; };
  const total = form ? form.ingredients.reduce((s, it) => s + it.qty * ingPrice(it.ingredient_id), 0) : 0;
  const cpu = form && form.yield_qty > 0 ? total / form.yield_qty : total;

  const defaultOutlet = () => outlet || outlets.find((o) => o.is_warehouse)?.id || outlets[0]?.id || "";

  const openForm = (b) => {
    setEditing(b || null);
    setForm(b ? {
      name: b.name, description: b.description || "", unit_id: b.unit_id || "", outlet_id: b.outlet_id || "",
      yield_qty: b.yield_qty, ingredients: b.ingredients.map((i) => ({ ingredient_id: i.ingredient_id, qty: i.qty })),
    } : { name: "", description: "", unit_id: "", outlet_id: defaultOutlet(), yield_qty: 1, ingredients: [] });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/bases/${editing.id}`, form);
      else await api.post("/bases", form);
      toast.success(t("save") + " OK");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/bases/${b.id}`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const produce = async () => {
    try {
      await api.post(`/bases/${produceFor.id}/produce`, { batches: Number(batches) || 1, outlet_id: produceOutlet || defaultOutlet() });
      toast.success("Produksi OK — stok bahan outlet terpilih berkurang, stok base bertambah");
      setProduceFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="bases-page">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_bases")}</h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="bases-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9 w-52" />
        </div>
        <Button data-testid="base-add-btn" onClick={() => openForm(null)} className="bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> {t("add")}</Button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {bases.map((b) => (
          <div key={b.id} data-testid={`base-card-${b.id}`} className="bg-card border border-border rounded-xl p-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center shrink-0"><Layers className="w-4.5 h-4.5 w-[18px] h-[18px]" /></div>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading font-semibold truncate">{b.name}</h3>
                <div className="text-xs text-muted-foreground">Yield {fmtNum(b.yield_qty)} {b.unit} · {b.ingredients.length} bahan · Stok <span className="font-mono font-semibold text-foreground">{fmtNum(b.stock || 0)} {b.unit}</span>{b.used_by ? ` · dipakai ${b.used_by} resep` : ""}</div>
                {b.by_outlet && Object.keys(b.by_outlet).length > 0 && (
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{Object.entries(b.by_outlet).map(([n, v]) => `${n}: ${fmtNum(v)}`).join(" · ")}</div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center mb-3">
              <div className="bg-muted/50 rounded-lg py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("recipeCost")}</div><div className="text-xs font-mono font-bold">{fmtIDR(b.total_cost)}</div></div>
              <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg py-2"><div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">{t("costPerUnit")}</div><div className="text-xs font-mono font-bold text-amber-700 dark:text-amber-400">{fmtIDR(b.cost_per_unit)}/{b.unit}</div></div>
            </div>
            <div className="text-xs text-muted-foreground mb-3 truncate">{b.ingredients.map((i) => i.name).join(", ")}</div>
            <div className="flex gap-1.5">
              <Button size="sm" data-testid={`base-produce-${b.id}`} onClick={() => { setProduceFor(b); setBatches(1); setProduceOutlet(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Factory className="w-3.5 h-3.5 mr-1" />{t("produce")}</Button>
              <Button size="sm" variant="outline" data-testid={`base-edit-${b.id}`} onClick={() => openForm(b)} className="flex-1"><Pencil className="w-3.5 h-3.5 mr-1" />{t("edit")}</Button>
              <Button size="sm" variant="outline" data-testid={`base-delete-${b.id}`} onClick={() => remove(b)} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {bases.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16">-</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="base-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("add")} Base</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</Label>
                  <Input data-testid="base-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Base Coffee / Base Milk / Base Syrup..." className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("unit")} (output)</Label>
                  <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger data-testid="base-unit-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("yieldQty")}</Label>
                  <Input data-testid="base-yield-input" type="number" value={form.yield_qty} onChange={(e) => setForm({ ...form, yield_qty: Number(e.target.value) })} className="mt-1.5" /></div>
                {!editing && (
                  <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("outlet")} (produksi batch pertama)</Label>
                    <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                      <SelectTrigger data-testid="base-outlet-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                      <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                )}
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Deskripsi</Label>
                  <Textarea data-testid="base-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("ingredients")}</Label>
                  <Button size="sm" variant="outline" data-testid="base-add-ingredient-btn" onClick={() => setForm({ ...form, ingredients: [...form.ingredients, { ingredient_id: "", qty: 1 }] })}><Plus className="w-3.5 h-3.5 mr-1" />{t("addIngredient")}</Button>
                </div>
                <div className="space-y-2">
                  {form.ingredients.map((it, idx) => (
                    <div key={it.ingredient_id || `empty-${idx}`} className="flex items-center gap-2" data-testid={`base-ingredient-row-${idx}`}>
                      <Select value={it.ingredient_id} onValueChange={(v) => { const arr = [...form.ingredients]; arr[idx] = { ...arr[idx], ingredient_id: v }; setForm({ ...form, ingredients: arr }); }}>
                        <SelectTrigger data-testid={`base-ing-select-${idx}`} className="flex-1"><SelectValue placeholder="..." /></SelectTrigger>
                        <SelectContent>{ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input data-testid={`base-ing-qty-${idx}`} type="number" value={it.qty} onChange={(e) => { const arr = [...form.ingredients]; arr[idx] = { ...arr[idx], qty: Number(e.target.value) }; setForm({ ...form, ingredients: arr }); }} className="w-24" />
                      <div className="w-24 text-xs font-mono text-muted-foreground">{fmtIDR(it.qty * ingPrice(it.ingredient_id))}</div>
                      <button onClick={() => setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) })} className="p-1.5 text-muted-foreground hover:text-rose-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-center bg-muted/40 rounded-xl p-3">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("recipeCost")}</div><div className="font-mono font-bold" data-testid="base-total-cost">{fmtIDR(total)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">{t("costPerUnit")}</div><div className="font-mono font-bold text-amber-700 dark:text-amber-400" data-testid="base-cost-per-unit">{fmtIDR(cpu)}</div></div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                {editing
                  ? "Mengubah komposisi/yield otomatis menyesuaikan stok bahan sesuai stok base yang ada: penambahan bahan memotong stok, pengurangan bahan mengembalikan stok."
                  : "Membuat base otomatis memproduksi 1 batch — stok bahan baku langsung berkurang dan menjadi stok base. Penjualan produk hanya memotong stok base."}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                <Button onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="base-save-btn">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!produceFor} onOpenChange={() => setProduceFor(null)}>
        <DialogContent className="max-w-sm" data-testid="base-produce-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("produce")} — {produceFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">1 batch = {fmtNum(produceFor?.yield_qty)} {produceFor?.unit} · Stok saat ini: <span className="font-mono font-semibold">{fmtNum(produceFor?.stock || 0)} {produceFor?.unit}</span></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("batches")}</Label>
              <Input data-testid="produce-batches-input" type="number" min="1" value={batches} onChange={(e) => setBatches(e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("outlet")}</Label>
              <Select value={produceOutlet || defaultOutlet()} onValueChange={setProduceOutlet}>
                <SelectTrigger data-testid="produce-outlet-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              Produksi memotong stok bahan baku sesuai komposisi base dan menambah stok base.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setProduceFor(null)}>{t("cancel")}</Button>
              <Button data-testid="produce-save-btn" onClick={produce} className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("produce")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
